import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ArrowRight, Mail, BookOpen, User, FileText } from "lucide-react";

// ============================================================================
//  /start — The "Link in Bio" Hub Page
//  ----------------------------------------------------------------------------
//  Built specifically for Instagram / Pinterest / YouTube bio links.
//  Primary KPI: email subscribers to the "Tap-in 12 month Calendar" group.
//  Secondary: hub links to blog, book, about so warm visitors can still browse.
//
//  Backend already routes default subscribes to the calendar group via
//  MAILERLITE_DEFAULT_GROUP_ID = 186110662635684873 — no group_id needed here.
// ============================================================================

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OCEAN = {
  deep: "#1B6B6E",
  mid: "#3D9096",
  light: "#7BC8CB",
  cream: "#F4F1EC",
  mint: "#EBF6F6",
  dark: "#1C2B2B",
};

export default function Start() {
  const [searchParams] = useSearchParams();
  const fromSource = searchParams.get("from") || "bio-link";

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "start_page_view", {
        from: fromSource,
        utm_source: searchParams.get("utm_source") || "(none)",
        utm_medium: searchParams.get("utm_medium") || "(none)",
        utm_campaign: searchParams.get("utm_campaign") || "(none)",
      });
    }
    // Pinterest Tag — PageVisit
    if (typeof window !== "undefined" && typeof window.pintrk === "function") {
      window.pintrk("track", "pagevisit");
    }
  }, [fromSource, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        firstName: firstName || "Friend",
        email: email.trim().toLowerCase(),
        group_id: "186110662635684873", // Tap-in 12 month Calendar group — explicit
        source: `start-page-${fromSource}`,
        utm_source: searchParams.get("utm_source") || null,
        utm_medium: searchParams.get("utm_medium") || null,
        utm_campaign: searchParams.get("utm_campaign") || null,
        utm_content: searchParams.get("utm_content") || null,
        utm_term: searchParams.get("utm_term") || null,
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
      });

      // GA4 conversion event
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "optin_submit", {
          method: "start_page_calendar",
          from: fromSource,
          utm_source: searchParams.get("utm_source") || "(direct)",
          utm_medium: searchParams.get("utm_medium") || "(none)",
          utm_campaign: searchParams.get("utm_campaign") || "(none)",
          value: 0,
          currency: "USD",
        });
        window.gtag("event", "generate_lead", { currency: "USD", value: 0 });
      }
      // Meta Pixel — Lead conversion (safe no-op until pixel base loads)
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "Free Calendar", content_category: "lead-magnet", value: 0, currency: "USD" });
      }
      // Mark this device as a returning subscriber so homepage swaps to the "welcome back" hero
      try { localStorage.setItem("mbsh_subscribed", "true"); } catch (_) { /* ignore */ 
      }
      // Pinterest Tag — Lead conversion
      if (typeof window !== "undefined" && typeof window.pintrk === "function") {
        window.pintrk("track", "lead", { lead_type: "email_optin" });
      }

      window.location.href = "/start-success";
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail || "Something hiccuped. Try once more, Lovely."
      );
    }
  };

  return (
    <div className="min-h-screen" style={{ background: OCEAN.cream }} data-testid="start-page">
      <Helmet>
        <title>Start Here — Free 12-Month I GET TO Calendar · Temple Franklin</title>
        <meta
          name="description"
          content="Get the free 12-Month I GET TO Calendar — one printable page per month with a simple Mind Body Spirit HYGIENE™ practice. Yours instantly when you join."
        />
        <link rel="canonical" href="https://mindbodyspirithygienetools.com/start" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Start Here — Free 12-Month I GET TO Calendar" />
        <meta property="og:description" content="One printable page per month. Twelve months of HYGIENE practice for the woman in the messy middle. Free." />
        <meta property="og:url" content="https://mindbodyspirithygienetools.com/start" />
        <meta property="og:image" content="https://mindbodyspirithygienetools.com/share-card.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* ─── HERO + OPT-IN ─── */}
      <section
        className="relative pt-16 md:pt-20 pb-12 md:pb-16 overflow-hidden"
        style={{ background: `linear-gradient(165deg, ${OCEAN.deep} 0%, ${OCEAN.mid} 100%)` }}
        data-testid="start-hero"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(circle at 80% 20%, rgba(123,200,203,0.22) 0%, transparent 55%)` }}
        />

        <div className="relative max-w-2xl mx-auto px-5 md:px-8 text-center" style={{ color: OCEAN.cream }}>
          {/* Logo / brand mark */}
          <Link to="/" data-testid="start-logo-link" className="inline-block mb-5">
            <img
              src="/downloads/MBSH-logo-TM-e4476e.png"
              alt="Mind Body Spirit HYGIENE"
              className="h-12 md:h-14 w-auto mx-auto"
            />
          </Link>

          <p
            className="font-sans text-[10px] md:text-[11px] tracking-[0.35em] uppercase font-semibold mb-3"
            style={{ color: OCEAN.light }}
            data-testid="start-eyebrow"
          >
            ✦ Free · Twelve months · One small reframe a day
          </p>

          <h1
            className="font-serif tracking-tighter leading-[1.05] mb-5"
            style={{ fontSize: "clamp(1.85rem, 5vw, 3.25rem)" }}
            data-testid="start-headline"
          >
            You're not behind.
            <br />
            You're not broken.
            <br />
            <span className="italic" style={{ color: OCEAN.light }}>
              You're in the messy middle.
            </span>
          </h1>

          <p
            className="font-serif italic text-base md:text-lg mb-6 max-w-xl mx-auto"
            style={{ color: "rgba(244,241,236,0.88)" }}
            data-testid="start-subhead"
          >
            And there's a real tool for exactly this — the free 12-Month I GET TO Calendar, one "I GET TO" reframe for every day of the year, plus my weekly walk-with-me emails.
          </p>

          {status !== "success" && (
            <form
              onSubmit={handleSubmit}
              className="space-y-3 max-w-md mx-auto mb-3"
              data-testid="start-optin-form"
            >
              <input
                type="text"
                placeholder="First name (optional)"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-5 py-4 rounded-full font-sans text-base focus:outline-none"
                style={{ background: "white", color: OCEAN.dark, border: "2px solid rgba(244,241,236,0.4)" }}
                data-testid="start-firstname-input"
              />
              <input
                type="email"
                placeholder="Your best email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-5 py-4 rounded-full font-sans text-base focus:outline-none"
                style={{ background: "white", color: OCEAN.dark, border: "2px solid rgba(244,241,236,0.4)" }}
                data-testid="start-email-input"
              />
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-sans font-bold tracking-wide shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: OCEAN.cream, color: OCEAN.deep }}
                data-testid="start-submit-btn"
              >
                {status === "submitting" ? (
                  "Sending…"
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    Send me the free calendar
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              {errorMsg && (
                <p className="font-sans text-sm pt-2" style={{ color: "#FECACA" }} data-testid="start-error-msg">
                  {errorMsg}
                </p>
              )}
            </form>
          )}

          <p
            className="font-sans text-[11px] md:text-xs leading-relaxed max-w-md mx-auto"
            style={{ color: "rgba(244,241,236,0.7)" }}
            data-testid="start-microcopy"
          >
            Free. No spam. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* ─── HUB LINKS (warmer visitors who already opted in or just want to browse) ─── */}
      <section className="px-5 md:px-8 py-10 md:py-14" data-testid="start-hub-links">
        <div className="max-w-md mx-auto space-y-3">
          <p
            className="font-sans text-[10px] tracking-[0.3em] uppercase font-semibold text-center mb-5"
            style={{ color: OCEAN.deep, opacity: 0.7 }}
          >
            Or wander a while
          </p>

          <Link
            to="/book"
            data-testid="start-link-book"
            className="flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border-2 transition-all hover:-translate-y-0.5 hover:shadow-md"
            style={{ borderColor: OCEAN.deep, background: "white", color: OCEAN.deep }}
          >
            <span className="flex items-center gap-3">
              <BookOpen className="w-5 h-5" style={{ color: OCEAN.deep }} />
              <span className="font-serif text-base md:text-lg">The Book</span>
            </span>
            <ArrowRight className="w-4 h-4" />
          </Link>

          <Link
            to="/blog"
            data-testid="start-link-blog"
            className="flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border-2 transition-all hover:-translate-y-0.5 hover:shadow-md"
            style={{ borderColor: OCEAN.deep, background: "white", color: OCEAN.deep }}
          >
            <span className="flex items-center gap-3">
              <FileText className="w-5 h-5" style={{ color: OCEAN.mid }} />
              <span className="font-serif text-base md:text-lg">Read the Blog</span>
            </span>
            <ArrowRight className="w-4 h-4" />
          </Link>

          <Link
            to="/about"
            data-testid="start-link-about"
            className="flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border-2 transition-all hover:-translate-y-0.5 hover:shadow-md"
            style={{ borderColor: OCEAN.deep, background: "white", color: OCEAN.deep }}
          >
            <span className="flex items-center gap-3">
              <User className="w-5 h-5" style={{ color: OCEAN.deep }} />
              <span className="font-serif text-base md:text-lg">Meet Temple</span>
            </span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="px-5 py-8 border-t" style={{ borderColor: "rgba(27,107,110,0.12)" }}>
        <div className="max-w-2xl mx-auto text-center">
          <p
            className="font-sans text-[10px] tracking-[0.3em] uppercase font-semibold"
            style={{ color: OCEAN.deep, opacity: 0.7 }}
          >
            © Mind Body Spirit HYGIENE™ · Temple Franklin
          </p>
          <p
            className="font-serif italic text-sm mt-2"
            style={{ color: OCEAN.deep, opacity: 0.7 }}
          >
            Real tools. Real women. In the messy middle.
          </p>
        </div>
      </footer>
    </div>
  );
}
